import React, { useEffect, useState, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { 
  Grid, 
  LinearProgress, 
  Typography, 
  Button, 
  Divider, 
  Card, 
  CardHeader, 
  CardContent, 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Tooltip
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import MessageIcon from "@material-ui/icons/Message";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import officialCampaignService from "../../services/officialCampaignService";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  summaryCards: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardIcon: {
    color: theme.palette.primary.main,
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 500,
    margin: theme.spacing(2, 0),
  },
  detailsCard: {
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    color: '#fff',
    fontWeight: 'bold',
  },
  deliveredChip: {
    backgroundColor: theme.palette.success.main,
  },
  pendingChip: {
    backgroundColor: theme.palette.warning.main,
  },
  failedChip: {
    backgroundColor: theme.palette.error.main,
  },
  pieChartContainer: {
    height: 250,
    position: 'relative',
    marginTop: theme.spacing(2),
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
}));

const OfficialCampaignReport = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Dados para o gráfico de pizza
  const [chartData, setChartData] = useState({
    labels: ['Entregues', 'Aguardando', 'Falhas'],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  });
  
  // Opções para o gráfico de pizza
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const fetchCampaign = async () => {
    try {
      const { data } = await officialCampaignService.show(id);
      setCampaign(data);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await officialCampaignService.stats(id);
      setStats(data);
      
      // Atualizar dados do gráfico
      setChartData({
        labels: ['Entregues', 'Aguardando', 'Falhas'],
        datasets: [
          {
            data: [data.sent || 0, data.pending || 0, data.failed || 0],
            backgroundColor: [
              'rgba(75, 192, 192, 0.7)',
              'rgba(255, 206, 86, 0.7)',
              'rgba(255, 99, 132, 0.7)',
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(255, 99, 132, 1)',
            ],
            borderWidth: 1,
          },
        ],
      });
    } catch (err) {
      toastError(err);
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const { data } = await officialCampaignService.contacts(id, {
        pageNumber: page + 1,
        pageSize: rowsPerPage
      });
      setContacts(data.records || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaign();
    fetchStats();
  }, [id]);

  useEffect(() => {
    fetchContacts();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const companyId = user?.companyId;
    if (!socket || !companyId) return;

    const handler = payload => {
      if (payload.record?.id === Number(id)) {
        setCampaign(payload.record);
        fetchStats();
      }
    };

    socket.on(`company-${companyId}-official-campaign`, handler);
    return () => socket.off(`company-${companyId}-official-campaign`, handler);
  }, [socket, user?.companyId, id]);

  const formatStatus = (val) => {
    switch (val) {
      case "scheduled":
        return i18n.t("campaignReport.scheduled");
      case "running":
        return i18n.t("campaignReport.process");
      case "finished":
        return i18n.t("campaignReport.finished");
      case "cancelled":
        return i18n.t("campaignReport.cancelled");
      default:
        return val;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "finished":
        return "primary";
      case "running":
        return "secondary";
      case "scheduled":
        return "default";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const getMessageStatusChip = (status) => {
    switch (status) {
      case 'sent':
        return (
          <Chip 
            label={i18n.t("campaignReport.delivered")} 
            size="small" 
            icon={<DoneAllIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.deliveredChip}`} 
          />
        );
      case 'pending':
      case 'queued':
        return (
          <Chip 
            label={i18n.t("campaignReport.pending")} 
            size="small" 
            icon={<ScheduleIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.pendingChip}`} 
          />
        );
      case 'failed':
        return (
          <Chip 
            label={i18n.t("campaignReport.failed")} 
            size="small" 
            icon={<ErrorIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.failedChip}`} 
          />
        );
      default:
        return (
          <Chip 
            label={status || i18n.t("campaignReport.unknown")} 
            size="small" 
            variant="outlined" 
          />
        );
    }
  };

  const formatPhoneNumber = (number) => {
    if (!number) return '';
    
    if (number.length > 15) {
      return `Grupo (${number.substring(0, 6)}...)`;
    }
    
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    return number;
  };

  const exportToExcel = async () => {
    try {
      toast.info("Gerando relatório Excel, aguarde...");
      
      let allContacts = [];
      let currentPage = 1;
      let hasMoreData = true;
      const pageSize = 1000;
      
      while (hasMoreData) {
        const { data } = await officialCampaignService.contacts(id, {
          pageNumber: currentPage,
          pageSize: pageSize
        });
        
        if (!data || !data.records || data.records.length === 0) {
          break;
        }
        
        allContacts = [...allContacts, ...data.records];
        hasMoreData = currentPage < (data.totalPages || 1);
        currentPage++;
        
        toast.info(`Carregando dados... ${allContacts.length} registros encontrados`);
      }
      
      if (allContacts.length === 0) {
        toast.error("Nenhum dado encontrado para exportar");
        return;
      }

      const excelData = [
        ["Relatório de Campanha Oficial", "", "", "", "", ""],
        [`Campanha: ${campaign?.name || 'N/A'}`, "", "", "", "", ""],
        [`Data de Geração: ${new Date().toLocaleString('pt-BR')}`, "", "", "", "", ""],
        ["", "", "", "", "", ""],
        ["ID", "Destinatário", "Mensagem", "Enviado em", "Entregue em", "Status"],
      ];

      allContacts.forEach(contact => {
        const payload = typeof contact.payload === 'string' 
          ? JSON.parse(contact.payload) 
          : contact.payload;
        const messageText = payload?.components?.find(c => c.type === 'BODY')?.text || 
                           payload?.components?.find(c => c.type === 'body')?.text || 
                           'Template';
        
        excelData.push([
          contact.id || '',
          contact.phoneNumber || '',
          messageText.substring(0, 100),
          contact.lastAttemptAt ? new Date(contact.lastAttemptAt).toLocaleString('pt-BR') : '',
          contact.status === 'sent' && contact.lastAttemptAt 
            ? new Date(contact.lastAttemptAt).toLocaleString('pt-BR') 
            : '',
          contact.status === 'sent' ? 'Entregue' : 
          contact.status === 'failed' ? 'Falha' : 'Pendente'
        ]);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      ws['!cols'] = [
        { wch: 15 },
        { wch: 20 },
        { wch: 50 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 }
      ];

      ws['!merges'] = [
        { s: { c: 0, r: 0 }, e: { c: 5, r: 0 } },
        { s: { c: 0, r: 1 }, e: { c: 5, r: 1 } },
        { s: { c: 0, r: 2 }, e: { c: 5, r: 2 } }
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Campanha");

      const fileName = `relatorio_campanha_oficial_${(campaign?.name || 'campanha').replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(wb, fileName);
      
      toast.success(`Relatório Excel gerado com sucesso! ${allContacts.length} registros exportados.`);
      
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error("Erro ao gerar relatório Excel");
    }
  };

  const sent = stats?.sent || 0;
  const pending = stats?.pending || 0;
  const failed = stats?.failed || 0;
  const totalContacts = stats?.totalContacts || campaign?.totalContacts || 0;

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} item style={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push('/official-campaigns')}
              startIcon={<ArrowBackIcon />}
            >
              Voltar
            </Button>
            <Title>Relatório de {campaign?.name || "Campanha Oficial"}</Title>
          </Grid>
        </Grid>
      </MainHeader>
      
      <Paper className={classes.mainPaper} variant="outlined">
        <Box mb={2}>
          <Typography variant="h5" component="h2" gutterBottom>
            {campaign?.name || "Campanha Oficial"}
          </Typography>
          <Chip 
            label={formatStatus(campaign?.status)} 
            color={getStatusColor(campaign?.status)}
            style={{ fontWeight: 'bold', marginRight: 8 }}
          />
        </Box>
        
        <Grid container spacing={3} className={classes.summaryCards}>
          {/* Card de Status */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Status de Entrega
                </Typography>
                <Box className={classes.pieChartContainer}>
                  <Doughnut data={chartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Cards com estatísticas principais */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <SendIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {sent}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Enviadas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <PhoneIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {totalContacts}
                </Typography>
                <Typography color="textSecondary">
                  Destinatários Únicos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <CheckCircleIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {sent}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Entregues
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Barra de progresso */}
        <Box className={classes.progressContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="body2" color="textSecondary">Progresso</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textPrimary">
                {sent} de {totalContacts}
              </Typography>
            </Grid>
          </Grid>
          <LinearProgress
            variant="determinate"
            style={{ height: 10, borderRadius: 5, margin: "8px 0" }}
            value={totalContacts > 0 ? (sent / totalContacts) * 100 : 0}
          />
        </Box>
        
        {/* Informações adicionais da campanha */}
        <Grid container spacing={3}>
          {campaign?.whatsappId && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <WhatsAppIcon />
                    </Avatar>
                  }
                  title="Conexão WhatsApp"
                  subheader={campaign.whatsapp?.name || "Não especificado"}
                />
              </Card>
            </Grid>
          )}
          
          {campaign?.contactListId && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <ListAltIcon />
                    </Avatar>
                  }
                  title="Lista de Contatos"
                  subheader={campaign.contactList?.name || "Não especificado"}
                />
              </Card>
            </Grid>
          )}
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <ScheduleIcon />
                  </Avatar>
                }
                title="Agendamento"
                subheader={campaign?.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "Não agendado"}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <EventAvailableIcon />
                  </Avatar>
                }
                title="Conclusão"
                subheader={campaign?.completedAt ? datetimeToClient(campaign.completedAt) : "Não concluído"}
              />
            </Card>
          </Grid>
        </Grid>
        
        {/* Seção de detalhes de mensagens */}
        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <MessageIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Detalhes das Mensagens
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={exportToExcel}
              style={{
                borderRadius: 20,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
              }}
            >
              Exportar Excel
            </Button>
          </Box>
          <Divider />
          
          <TableContainer className={classes.tableContainer}>
            <Table aria-label="tabela de mensagens">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Destinatário</TableCell>
                  <TableCell>Mensagem</TableCell>
                  <TableCell>Enviado em</TableCell>
                  <TableCell>Entregue em</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => {
                  const payload = typeof contact.payload === 'string' 
                    ? JSON.parse(contact.payload) 
                    : contact.payload;
                  const messageText = payload?.components?.find(c => c.type === 'BODY' || c.type === 'body')?.text || 
                                     payload?.components?.find(c => c.type === 'BODY' || c.type === 'body')?.parameters?.[0]?.text ||
                                     'Template';
                  
                  return (
                    <TableRow key={contact.id}>
                      <TableCell>{contact.id}</TableCell>
                      <TableCell>
                        <Tooltip title={contact.phoneNumber}>
                          <span>{formatPhoneNumber(contact.phoneNumber)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={messageText}>
                          <span>{messageText.substring(0, 50)}{messageText.length > 50 ? '...' : ''}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {contact.lastAttemptAt ? datetimeToClient(contact.lastAttemptAt) : '-'}
                      </TableCell>
                      <TableCell>
                        {contact.status === 'sent' && contact.lastAttemptAt 
                          ? datetimeToClient(contact.lastAttemptAt) 
                          : '-'}
                      </TableCell>
                      <TableCell>{getMessageStatusChip(contact.status)}</TableCell>
                    </TableRow>
                  );
                })}
                {contacts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {loading ? "Carregando..." : "Nenhuma mensagem encontrada"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalContacts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => {
                setPage(newPage);
              }}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default OfficialCampaignReport;
